<script setup lang="ts">
  import Table from '@/components/table/Table.vue'
  import TableColumnSheet from '@/components/table/TableColumnSheet.vue'
  import TopBar from '@/components/TopBar.vue'
  import { useProjectValidation } from '@/composables/validation/use-project-validation'
  import { useProjectStore } from '@/store/project.store'
  import { useTemplateStore } from '@/store/template.store'
  import { createEmptyProject, type Project } from '@/types/nest/projects'
  import {
    ConfirmationDialog,
    FormFieldContainer,
    FormInput,
    isUuid,
    UiButton,
    useToastStore,
    type Column,
    type Template,
  } from '@logistiek-zonder-papier/lzp-library'
  import { computed, onMounted, ref } from 'vue'
  import { useRoute, useRouter } from 'vue-router'

  const projectStore = useProjectStore()
  const toastStore = useToastStore()
  const templateStore = useTemplateStore()
  const route = useRoute()
  const router = useRouter()

  const columns: Column[] = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Naam', searchable: true },
    { key: 'number', label: 'Template Nummer', searchable: true },
    { key: 'fromAddress.street2', label: 'Herkomst', searchable: true },
    { key: 'toAddress.street2', label: 'Bestemming', searchable: true },
    { key: 'actions', label: 'Acties', sticky: 'right' },
  ]

  const visibleColumns = ref<Column[]>([])
  const showColumnSheet = ref<boolean>(false)

  const projectCopy = ref<Project>(createEmptyProject())
  const templates = ref<Template[]>([])
  const showDeleteModal = ref(false)

  const templateRows = computed(() =>
    templates.value.map(template => ({
      ...template,
      number: template.bgb?.groupId ?? template.ecmr?.domainIdentifier ?? 'N/A',
    }))
  )

  const hasNameChanges = computed(() => {
    if (!projectStore.selectedProject || !projectCopy.value) {
      return false
    }
    return projectStore.selectedProject.name !== projectCopy.value.name
  })

  onMounted(async () => {
    const projectId = route.params['uuid']
    if (!isUuid(projectId)) {
      throw new Error('Project ID is invalid')
    }

    await projectStore.fetchProject(projectId)
    templates.value = await templateStore.fetchByProject(projectId)

    if (projectStore.selectedProject) {
      projectCopy.value = { ...projectStore.selectedProject }
    }
  })

  const { validate, visibleErrors, isSubmittedAndInvalid } = useProjectValidation(
    computed(() => projectStore.selectedProject)
  )

  async function updateProjectName() {
    if (!projectStore.selectedProject) return
    if (!validate()) return
    try {
      await projectStore.updateProject(projectStore.selectedProject)
      projectCopy.value = { ...projectStore.selectedProject }

      toastStore.success('Project data successfully updated')
    } catch (error) {
      console.error('Error updating project name:', error)
    }
  }

  async function deleteProject() {
    await projectStore.deleteProject(`${route.params['uuid']}`)
    router.back()
  }
</script>

<template>
  <TopBar>
    <UiButton v-if="templateRows.length > 0" icon="more" @click="showColumnSheet = true" />

    <UiButton
      variant="prominent"
      icon="plus"
      @click="
        router.push({
          name: 'template-new-project',
          params: { projectId: projectStore.selectedProject?.id },
        })
      "
    />
  </TopBar>

  <TableColumnSheet
    v-model:show="showColumnSheet"
    v-model:visible-columns="visibleColumns"
    :columns="columns"
  />

  <div class="layout">
    <div v-if="projectStore.selectedProject" class="project-container">
      <FormFieldContainer label="Name">
        <template #action-button>
          <UiButton variant="negative" @click="showDeleteModal = true" icon="delete" />
        </template>
        <FormInput v-model="projectStore.selectedProject.name" :error="visibleErrors.name" />
      </FormFieldContainer>

      <div class="project-button">
        <UiButton
          label="Opslaan"
          variant="prominent"
          @click="updateProjectName"
          :disabled="!hasNameChanges || isSubmittedAndInvalid"
        />
      </div>
    </div>

    <Table :rows="templateRows" :columns="visibleColumns" row-key="id">
      <template #cell-actions="{ row }">
        <UiButton
          variant="link"
          icon="edit"
          @click="router.push({ name: 'template-edit', params: { templateId: row.id } })"
        />
      </template>
    </Table>
  </div>

  <ConfirmationDialog
    :title="`Are you sure you want to remove: ${projectCopy?.name}?`"
    body="This action cannot be undone."
    confirmButtonText="Remove"
    confirmButtonColor="negative"
    v-model:show="showDeleteModal"
    :confirm="() => deleteProject()"
  />
</template>

<style scoped lang="css">
  .layout {
    display: flex;
    padding: var(--spacing-m);
    flex-direction: column;
    gap: var(--spacing-m);
    min-height: calc(100dvh - var(--top-bar-height));
  }

  .project-button {
    justify-items: end;
  }
</style>
